/**
 * Review Queue domain types. Field names and shapes must match the backend
 * gate contract exactly:
 *
 *   GET  /v1/actions?page=&limit=&sort=&verdict=&tier=&actionType=&q=&assignment=&urgency=&environment=&view=
 *   GET  /v1/actions/{action_id}
 *   POST /v1/actions/{action_id}/decision
 *
 * `ReviewItem`/`ReviewItemDetail` are the `data` shapes returned by the list
 * and detail endpoints respectively. The backend persists the full review
 * snapshot; optional fields keep older rows forward-compatible.
 */
import type {
  ActionType,
  CheckResult,
  ConsequenceTier,
  Reversibility,
} from "@/lib/vocabulary";

import { ACTION_TYPE, CONSEQUENCE_TIER } from "@/lib/vocabulary";

export const ACTION_TYPE_VALUES: readonly ActionType[] = Object.values(
  ACTION_TYPE,
) as ActionType[];

export const CONSEQUENCE_TIER_VALUES: readonly ConsequenceTier[] = Object.values(
  CONSEQUENCE_TIER,
) as ConsequenceTier[];

export interface ReviewReason {
  code: string;
  message: string;
  ruleRef: string;
}

export type ReviewStatus = "OPEN" | "CLAIMED" | "IN_REVIEW" | "DECIDED" | "EXPIRED";
export type ExecutionStatus =
  | "HELD"
  | "REVALIDATING"
  | "RELEASED"
  | "COMMITTING"
  | "COMMITTED"
  | "COMMIT_FAILED"
  | "OUTCOME_UNKNOWN"
  | "RECONCILIATION_REQUIRED";

export interface ReviewAssignee {
  id: string;
  name: string;
  role: string;
}

export interface ReviewCheck {
  id: string;
  category: string;
  name: string;
  result: CheckResult | (string & {});
  summary: string;
  evidence: string;
  evaluatedAt: string;
}

export interface ReviewTraceEvent {
  id: string;
  timestamp: string;
  kind: "agent" | "tool" | "check" | "system" | "human" | (string & {});
  title: string;
  summary: string;
  actor: string;
  status: "complete" | "held" | "warning" | (string & {});
}

export interface ReviewAuditEvent {
  id: string;
  timestamp: string;
  event: string;
  actor: string;
  summary: string;
}

export interface ReviewRecommendation {
  disposition: "APPROVE" | "REJECT" | "REVIEW" | (string & {});
  summary: string;
  confidence: string;
}

export const REVIEW_DETAIL_TAB = {
  OVERVIEW: "overview",
  EVIDENCE: "evidence",
  TRACE: "trace",
} as const;
export type ReviewDetailTab = (typeof REVIEW_DETAIL_TAB)[keyof typeof REVIEW_DETAIL_TAB];
export const REVIEW_DETAIL_TAB_VALUES: readonly ReviewDetailTab[] = Object.values(
  REVIEW_DETAIL_TAB,
) as ReviewDetailTab[];

/**
 * A single action held for human review. The queue only ever surfaces
 * ESCALATE-verdict actions — `verdict` is still typed loosely (`string`) so
 * a future/unexpected value can never fail to type-check; see
 * `list/verdict-badge.tsx` for how that gets handled at render time.
 */
export interface ReviewItem {
  actionId: string;
  actionType: ActionType | (string & {});
  verdict: string;
  consequenceTier: ConsequenceTier;
  reasons: ReviewReason[];
  deadlineAt: string | null;
  requestedAt: string;
  rulepackVersion: string;
  applicationName?: string;
  applicationId?: string;
  environment?: string;
  workflowName?: string;
  agentName?: string;
  summary?: string;
  reviewStatus?: ReviewStatus;
  executionStatus?: ExecutionStatus;
  assignee?: ReviewAssignee;
  isAssignedToCurrentReviewer?: boolean;
  version?: number;
}

export interface ReviewItemDetail extends ReviewItem {
  payload: Record<string, unknown>;
  worldSnapshot: Record<string, unknown>;
  actorAgentId: string;
  traceId?: string;
  subjectLabel?: string;
  externalDestination?: string;
  expectedConsequence?: string;
  reversibility?: Reversibility | (string & {});
  evidenceFreshness?: string;
  worldSnapshotAt?: string;
  checks?: ReviewCheck[];
  traceEvents?: ReviewTraceEvent[];
  auditEvents?: ReviewAuditEvent[];
  recommendation?: ReviewRecommendation;
}

export const DECISION = {
  APPROVE: "APPROVE",
  APPROVE_WITH_EDIT: "APPROVE_WITH_EDIT",
  REJECT: "REJECT",
} as const;
export type Decision = (typeof DECISION)[keyof typeof DECISION];

/** Fixed reject-reason vocabulary — a reviewer must pick one of these, not free text alone. */
export const REJECT_REASON_CODE = {
  MISSING_CLINICAL_INDICATION: "MISSING_CLINICAL_INDICATION",
  WRONG_PAYER: "WRONG_PAYER",
  DUPLICATE_REQUEST: "DUPLICATE_REQUEST",
  POLICY_VIOLATION: "POLICY_VIOLATION",
  OTHER: "OTHER",
} as const;
export type RejectReasonCode =
  (typeof REJECT_REASON_CODE)[keyof typeof REJECT_REASON_CODE];

export const REJECT_REASON_CODE_VALUES: readonly RejectReasonCode[] = Object.values(
  REJECT_REASON_CODE,
) as RejectReasonCode[];

/** Body for `POST /v1/actions/{action_id}/decision`. */
export interface DecisionInput {
  decision: Decision;
  reasonCode: string;
  attestation?: string;
  editedPayload?: Record<string, unknown>;
  expectedVersion?: number;
}

/** Filter/search state for the queue list, independent of pagination and sort. */
export interface ReviewQueueFilters {
  q?: string;
  tier?: ConsequenceTier;
  actionType?: ActionType;
  assignment?: "all" | "assigned" | "unassigned";
  urgency?: "all" | "overdue" | "urgent";
  environment?: string;
}

export const REVIEW_QUEUE_VIEW = {
  ALL: "all",
  MINE: "mine",
  UNASSIGNED: "unassigned",
  URGENT: "urgent",
  HIGH_RISK: "high-risk",
} as const;
export type ReviewQueueView = (typeof REVIEW_QUEUE_VIEW)[keyof typeof REVIEW_QUEUE_VIEW];
export const REVIEW_QUEUE_VIEW_VALUES: readonly ReviewQueueView[] = Object.values(
  REVIEW_QUEUE_VIEW,
) as ReviewQueueView[];

export function hasActiveReviewQueueFilters(filters: ReviewQueueFilters): boolean {
  return Boolean(
    filters.q ||
    filters.tier !== undefined ||
    filters.actionType ||
    (filters.assignment && filters.assignment !== "all") ||
    (filters.urgency && filters.urgency !== "all") ||
    filters.environment,
  );
}

/** Column count, kept in sync with `ReviewTable`'s `<thead>` so the loading `TableSkeleton` never drifts from the real table shape. */
export const REVIEW_TABLE_COLUMN_COUNT = 9;

/** Below this remaining time, a deadline is rendered as urgent (red, bold) — the point at which a missed review has real consequences. */
export const URGENT_DEADLINE_THRESHOLD_MS = 24 * 60 * 60 * 1000;
